import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth, useOrganization } from '@clerk/clerk-react'

import { AGENT_URL } from '@/config/env'
import { fetchJsonWithAuth, mutateJsonWithAuth } from '@/lib/api'
import { machineKeys } from '@/lib/queryKeys'
import type { Machine, MachineConfig, MachineListResponse } from '../types'

const MACHINES_URL = `${AGENT_URL}/computer-use/machines`

export function useMachinesQuery() {
  const { getToken } = useAuth()
  const { organization } = useOrganization()

  return useQuery({
    queryKey: machineKeys.list(organization?.id),
    queryFn: async () => {
      const token = await getToken()
      if (!token) {
        throw new Error('No authentication token available')
      }
      return fetchJsonWithAuth<MachineListResponse>({
        token,
        url: MACHINES_URL,
        errorMessage: 'Failed to load machines',
      })
    },
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  })
}

export function useUpdateMachineConfig() {
  const { getToken } = useAuth()
  const { organization } = useOrganization()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      machineId,
      config,
    }: {
      machineId: string
      config: MachineConfig
    }) => {
      const token = await getToken()
      if (!token) {
        throw new Error('No authentication token available')
      }
      // machine_id contains "/" (e.g. nova-bcbs-1/bcbsmn); the route uses a
      // path converter, so the slash is passed through unencoded.
      return mutateJsonWithAuth<Machine>({
        token,
        url: `${MACHINES_URL}/${machineId}`,
        method: 'PATCH',
        body: { config },
        errorMessage: 'Failed to update config',
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: machineKeys.all(organization?.id),
      })
    },
  })
}
